import { observer } from "mobx-react";
import React from "react";

import { useFormContext, useFormState } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ErrorMessage, RequiredMark } from "@components/inputs/shared";
import classNames from "classnames";

export default observer(function GroupedDropdownOptions<
	D extends DataType,
	GD extends DataType,
>(props: GroupedDropdownOptionsProps<D, GD>) {
	const {
		name,
		label,
		placeholder,
		groupKeyName,
		groupData,
		data,
		required,
		message,
		labelClassName,
		className,
	} = props;

	const { register } = useFormContext();
	const { errors } = useFormState();
	const { t } = useTranslation();

	let error = errors?.name;

	const inputClass = classNames("input input--base input--select", {
		"input-validation-error": error != undefined,
	});

	return (
		<div className={className}>
			<label className={classNames("label", labelClassName)}>
				{t(label)} <RequiredMark required={required} />
			</label>
			<ErrorMessage name={name} />
			<select
				className={inputClass}
				{...register(name, {
					required: {
						value: !!required,
						message: message || "COMMON.REQUIRED_FIELD",
					},
				})}
			>
				<option value="">{t(placeholder)}</option>

				{groupData?.map((group) => (
					<optgroup key={group.id} label={group.name}>
						{data
							?.filter(
								(content) => group.id === content[groupKeyName]
							)
							.map((content) => (
								<option key={content.id} value={content.id}>
									{content.name}
								</option>
							))}
					</optgroup>
				))}
			</select>
		</div>
	);
});

type GroupedDropdownOptionsProps<
	Data extends DataType,
	GroupData extends DataType,
> = {
	groupKeyName: keyof Data;
	label: string;
	name: string;
	placeholder: string;
	data?: Data[];
	groupData?: GroupData[];
	className?: string;
	labelClassName?: string;
	message?: string;
	required?: boolean;
};

type DataType = {
	id: string;
	name: string;
};
